// src/services/lastLocation.ts
//
// Persists the user's last-known location to their user doc in
// Firestore. Read by the curator's 30-minute heartbeat scheduled
// function (Phase 3B). The heartbeat only fires for users where
// last_location.captured_at is fresh (within 60 minutes), so this
// write is what makes a user "available for heartbeat" on the
// server side.
//
// Design choices:
//
// - Debounced to ONCE PER 5 MINUTES per session. The
//   useGeolocation hook fires updates whenever the OS reports a
//   new fix (often every few seconds during walking). We don't
//   want to write to Firestore that often; a 5-minute resolution
//   is more than enough for the 30-minute heartbeat to find a
//   fresh-enough fix.
//
// - Skipped for anonymous users. The locked Phase 3 design has no
//   memory layer for anonymous users; their last_location would
//   be a write nobody reads.
//
// - Skipped when location accuracy is poor (>500m). Stale GPS or
//   coarse network-based fixes give bad heartbeat decisions
//   ("user is in {wrong city}"). We'd rather have no fix than a
//   wrong one.
//
// - Best-effort. Write failures are logged and ignored — the
//   user's product experience doesn't depend on this write
//   succeeding. The next 5-minute tick retries.

import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './firebase';

interface LastLocationInput {
  latitude: number;
  longitude: number;
  accuracy?: number;
}

const THROTTLE_MS = 5 * 60 * 1000; // 5 minutes
const MAX_ACCEPTABLE_ACCURACY_M = 500;

// Module-level state — survives across hook re-renders and across
// component remounts. Resets on full page reload, which is the
// right cadence (a fresh page load deserves a fresh write).
let lastWriteAt = 0;
let lastWrittenUid: string | null = null;
let lastWrittenLat: number | null = null;
let lastWrittenLng: number | null = null;

/** Try to write the user's last-known location. Throttled, gated
 *  by auth state and accuracy. Returns true if a write was
 *  attempted (regardless of success), false if skipped. */
export async function maybeWriteLastLocation(
  input: LastLocationInput
): Promise<boolean> {
  const user = auth.currentUser;
  if (!user) return false;
  if (user.isAnonymous) return false;

  // Accuracy gate. The geolocation hook's `accuracy` is in meters;
  // missing accuracy is treated as untrustworthy (likely a network-
  // based fix on a desktop with no GPS).
  const acc = typeof input.accuracy === 'number' ? input.accuracy : Infinity;
  if (acc > MAX_ACCEPTABLE_ACCURACY_M) return false;

  // Throttle. If we wrote recently, skip — UNLESS the position
  // moved meaningfully (>200m), in which case the user has clearly
  // moved and we want to capture the new spot promptly.
  const now = Date.now();
  const sameUser = lastWrittenUid === user.uid;
  const withinThrottle = now - lastWriteAt < THROTTLE_MS;
  const movedMeaningfully =
    lastWrittenLat !== null &&
    lastWrittenLng !== null &&
    haversineMeters(
      lastWrittenLat,
      lastWrittenLng,
      input.latitude,
      input.longitude
    ) > 200;
  if (sameUser && withinThrottle && !movedMeaningfully) {
    return false;
  }

  try {
    await setDoc(
      doc(db, 'users', user.uid),
      {
        last_location: {
          lat: input.latitude,
          lng: input.longitude,
          accuracy_m: acc === Infinity ? null : acc,
          captured_at: serverTimestamp(),
        },
        // Bump last_active_at while we're here — the user
        // clearly is. The 12-hour "user active" gate on the
        // heartbeat reads this field.
        last_active_at: serverTimestamp(),
      },
      { merge: true }
    );
    lastWriteAt = now;
    lastWrittenUid = user.uid;
    lastWrittenLat = input.latitude;
    lastWrittenLng = input.longitude;
    return true;
  } catch (err) {
    // Best-effort; do not surface to the user.
    console.warn('maybeWriteLastLocation failed:', err);
    return false;
  }
}

function haversineMeters(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.min(1, Math.sqrt(a))));
}
